/**
 * AI Insights API Service
 *
 * Standalone service layer for AI Insights API endpoints.
 * Uses the existing httpClient but is completely isolated from other modules.
 */

import { httpClient } from '@/src/@core/lib/httpClient'
import type {
  ExecuteProactiveActionRequest,
  ExecuteProactiveActionResponse
} from '@/src/types/apps/proactiveAnalysisTypes'
import type {
  ChatRequest,
  ChatResponse,
  ActionableInsightRequest,
  ActionableInsightResponse,
  ActionableInsightGenerateRequest,
  TrendAnalysisRequest,
  TrendAnalysisQueryRequest,
  TrendAnalysisResponse,
  SalesImpactRequest,
  SalesImpactResponse,
  ComparisonType,
  GroupedAbnormalitiesResponse,
  Abnormality,
  AIInsight
} from '../types/aiInsightsTypes'

const BASE_URL = '/insights'

class AIInsightsApiService {
  /**
   * Send a chat message to the AI assistant
   */
  async chat(request: ChatRequest): Promise<ChatResponse> {
    const response = await httpClient.post<{ data: ChatResponse }>(`${BASE_URL}/ai/chat`, request)

    return response.data.data
  }

  /**
   * Generate actionable insights for a problem (old endpoint - kept for backward compatibility)
   */
  async generateActionableInsight(request: ActionableInsightRequest): Promise<ActionableInsightResponse> {
    const response = await httpClient.post<{ data: ActionableInsightResponse }>(
      `${BASE_URL}/ai/actionable-insight`,
      request
    )

    return response.data.data
  }

  /**
   * Generate actionable insights (new endpoint)
   */
  async generateActionableInsights(request: ActionableInsightGenerateRequest): Promise<ActionableInsightResponse> {
    const response = await httpClient.post<{ data: ActionableInsightResponse }>(
      `${BASE_URL}/actionable-insights/generate`,
      request
    )

    return response.data.data
  }

  /**
   * Analyze trends
   */
  async analyzeTrends(request: TrendAnalysisRequest): Promise<TrendAnalysisResponse> {
    const response = await httpClient.post<{ data: { data: TrendAnalysisResponse } }>(
      `${BASE_URL}/trend-analysis/analyze`,
      request
    )

    return response.data.data.data
  }

  /**
   * Query trends with natural language
   */
  async queryTrends(request: TrendAnalysisQueryRequest): Promise<TrendAnalysisResponse> {
    const response = await httpClient.post<{ data: { data: TrendAnalysisResponse } }>(
      `${BASE_URL}/trend-analysis/query`,
      request
    )

    return response.data.data.data
  }

  /**
   * Get sales impact of abnormalities
   */
  async getSalesImpact(params: {
    startDate: string
    endDate: string
    comparisonType?: ComparisonType
    restaurantIds?: number[]
  }): Promise<SalesImpactResponse> {
    const response = await httpClient.get<{ data: { data: SalesImpactResponse } }>(`${BASE_URL}/sales-impact`, {
      params
    })

    return response.data.data.data
  }

  /**
   * Analyze sales impact from abnormality data
   */
  async analyzeSalesImpact(request: SalesImpactRequest): Promise<SalesImpactResponse> {
    const response = await httpClient.post<{ data: SalesImpactResponse }>(
      `${BASE_URL}/abnormality-sales-impact/analyze`,
      request
    )

    return response.data.data
  }

  /**
   * Get equipment abnormalities (optimized - fast)
   */
  async getEquipmentAbnormalities(): Promise<Abnormality[]> {
    const response = await httpClient.get<{ data: Abnormality[] }>(`${BASE_URL}/abnormalities/equipment`)

    return response.data.data || []
  }

  /**
   * Get ingredient abnormalities (optimized - fast)
   */
  async getIngredientAbnormalities(): Promise<Abnormality[]> {
    const response = await httpClient.get<{ data: Abnormality[] }>(`${BASE_URL}/abnormalities/ingredients`)

    return response.data.data || []
  }

  /**
   * Get channel abnormalities (optimized - fast, with enhanced universal check)
   */
  async getChannelAbnormalities(): Promise<Abnormality[]> {
    const response = await httpClient.get<{ data: Abnormality[] }>(
      `${BASE_URL}/abnormalities/channels?includeUniversalCheck=true`
    )

    return response.data.data || []
  }

  /**
   * Get AI insights for a specific abnormality type (on-demand)
   */
  async getAIInsightsForType(
    type: 'equipment' | 'ingredient' | 'channel' | 'operations',
    typeData: { totalRestaurantsAffected: number; totalItemsDisabled: number; restaurants: any[] }
  ): Promise<AIInsight | null> {
    try {
      const insightType =
        type === 'equipment'
          ? 'equipment_issue'
          : type === 'ingredient'
            ? 'ingredient_issue'
            : type === 'operations'
              ? 'trading_hours'
              : 'channel_issue'

      const response = await httpClient.post<{ data: ActionableInsightResponse }>(
        `${BASE_URL}/actionable-insights/generate`,
        {
          insightType,
          message: `Analyze ${type} abnormalities affecting ${typeData.totalRestaurantsAffected} restaurants with ${typeData.totalItemsDisabled} items disabled.`
        }
      )

      // Transform ActionableInsightResponse to AIInsight format
      const insight = response.data.data.insight

      return {
        problem: insight.problem,
        impact: {
          severity: insight.impact.severity,
          affectedRestaurants:
            typeof insight.impact.affectedRestaurants === 'number'
              ? insight.impact.affectedRestaurants
              : Array.isArray(insight.impact.affectedRestaurants)
                ? insight.impact.affectedRestaurants.length
                : typeData.totalRestaurantsAffected,
          estimatedRevenueImpact: insight.impact.estimatedRevenueImpact || 'Moderate impact',
          customerImpact: insight.impact.customerImpact || 'Potential service disruptions',
          operationalImpact: insight.impact.operationalImpact || 'Operational challenges detected'
        },
        details: {
          rootCause: insight.details.rootCause || 'Analysis in progress',
          affectedAreas: insight.details.affectedAreas || [],
          currentStatus: insight.details.currentStatus || 'Active issues detected',
          timeline: insight.details.timeline || 'Current ongoing issues'
        },
        actions: {
          immediate: insight.actions.immediate || [],
          shortTerm: insight.actions.shortTerm || [],
          longTerm: insight.actions.longTerm || []
        },
        prevention: {
          monitoring: insight.prevention.monitoring || [],
          processes: insight.prevention.processes || [],
          alerts: insight.prevention.alerts || []
        }
      }
    } catch (error) {
      console.error(`Failed to generate AI insights for ${type}:`, error)

      return null
    }
  }

  /**
   * Get grouped abnormalities using new multi-agent endpoint
   * Returns all abnormality types in one call (parallel agent execution)
   */
  async getGroupedAbnormalities(): Promise<GroupedAbnormalitiesResponse> {
    const response = await httpClient.get<any>(`${BASE_URL}/abnormalities/multi-agent`)

    // The API returns nested structure: data.data.data.agentResults
    const innerData = response.data?.data?.data || response.data?.data || {}
    const apiData = innerData.agentResults || []
    const apiSummary = innerData.summary || {}

    // Helper function to transform API aiInsight to expected AIInsight format
    const transformAIInsight = (apiInsight: any, item: any): AIInsight | undefined => {
      if (!apiInsight) return undefined

      // Extract action strings from recommendation objects
      // API returns: [{actionType, action, priority, expectedImpact}, ...]
      // We need: ["action string 1", "action string 2", ...]
      const recommendations = apiInsight.recommendations || []
      const actionStrings = recommendations.map((rec: any) =>
        typeof rec === 'string' ? rec : rec.action || rec.toString()
      )

      // New API format has flat structure with summary, analysis, recommendations, severity
      // Transform to expected nested AIInsight format
      return {
        problem: apiInsight.summary || `${item.affectedRestaurantCount} restaurants affected`,
        impact: {
          severity: apiInsight.severity || 'medium',
          affectedRestaurants: item.affectedRestaurantCount || 0,
          estimatedRevenueImpact: item.salesImpact?.estimatedRevenueLoss
            ? `$${item.salesImpact.estimatedRevenueLoss.toLocaleString()}`
            : 'Unknown',
          customerImpact: apiInsight.analysis || 'Potential service disruptions',
          operationalImpact: apiInsight.analysis || 'Operational issues detected'
        },
        details: {
          rootCause: apiInsight.analysis || 'Analysis in progress',
          affectedAreas: [],
          currentStatus: 'Active issues detected',
          timeline: 'Current ongoing issues'
        },
        actions: {
          immediate: actionStrings,
          shortTerm: [],
          longTerm: []
        },
        prevention: {
          monitoring: [],
          processes: [],
          alerts: []
        }
      }
    }

    // Transform API response to expected format
    const transformedData: GroupedAbnormalitiesResponse['data'] = apiData.map((item: any) => {
      // Get all unique item names from restaurants for this agent type
      // This is used to filter historical patterns to only show relevant items
      const actualItemNames = new Set<string>()
      ;(item.restaurants || []).forEach((r: any) => {
        ;(r.items || []).forEach((i: any) => {
          if (i.itemName) {
            actualItemNames.add(i.itemName.toLowerCase())
          }
        })
      })

      // Filter trendPatterns to only include items that actually exist in this agent's data
      // This works around a backend bug where channel agent returns ingredient/equipment items
      const filteredTrendPatterns = (item.trendPatterns || []).filter((pattern: any) => {
        const patternItemName = (pattern.itemName || pattern.name || '').toLowerCase()

        // If no items in restaurants, allow all patterns
        if (actualItemNames.size === 0) return true

        // Only include patterns whose item name matches actual items for this agent type
        return actualItemNames.has(patternItemName)
      })

      return {
        type: item.agentType,
        totalRestaurantsAffected: item.affectedRestaurantCount,
        totalItemsDisabled: item.totalAbnormalityCount,
        distinctItemCount: item.distinctItemCount,
        restaurants: (item.restaurants || []).map((r: any) => ({
          restaurantId: r.restaurantId,
          restaurantName: r.restaurantName,
          bhyveId: r.bhyveId,
          state: r.state,
          abnormalityCount: r.abnormalityCount,
          items: r.items?.map((i: any) => i.itemName) || [],
          itemDetails: r.items?.map((i: any) => i.reason || i.itemName) || [],
          itemsWithDetails:
            r.items?.map((i: any) => ({
              itemId: i.itemId,
              itemName: i.itemName,
              type: i.type,
              daysDisabled: i.daysDisabled || 0,
              reason: i.reason,
              disabledAt: i.disabledAt,
              disabledBy: i.disabledBy
            })) || []
        })),
        aiInsight: transformAIInsight(item.aiInsight, item),

        // Store raw salesImpact data for enhanced UI display
        salesImpact: item.salesImpact
          ? {
              estimatedRevenueLoss: item.salesImpact.estimatedRevenueLoss,
              dailyImpactRate: item.salesImpact.dailyImpactRate,
              projectedMonthlyImpact: item.salesImpact.projectedMonthlyImpact,
              calculationBasis: item.salesImpact.calculationBasis,
              confidenceLevel: item.salesImpact.confidenceLevel,
              averageDailyImpact: item.salesImpact.averageDailyImpact,
              currency: item.salesImpact.currency,
              impactPeriod: item.salesImpact.impactPeriod,
              impactPeriodDays: item.salesImpact.impactPeriodDays,
              restaurantImpacts:
                item.salesImpact.restaurantImpacts?.map((ri: any) => ({
                  restaurantId: ri.restaurantId,
                  restaurantName: ri.restaurantName,
                  bhyveId: ri.bhyveId,
                  previousPeriodSales: ri.previousPeriodSales,
                  currentPeriodSales: ri.currentPeriodSales,
                  estimatedLoss: ri.estimatedLoss,
                  averageDailySales: ri.averageDailySales,
                  daysAffected: ri.daysAffected,
                  comparisonPeriod: ri.comparisonPeriod
                })) || []
            }
          : undefined,

        // Store raw recommendations for enhanced display
        rawRecommendations: item.aiInsight?.recommendations || [],
        historicalPatternInsights: filteredTrendPatterns.map((pattern: any) => ({
          itemName: pattern.itemName || pattern.name || 'Unknown Item',
          itemType: pattern.itemType || pattern.type || item.agentType || 'unknown',
          historicalOccurrences:
            pattern.historicalOccurrences ||
            pattern.occurrenceCount ||
            pattern.occurrences ||
            pattern.count ||
            pattern.frequency ||
            pattern.totalOccurrences ||
            0,
          mostCommonReason: pattern.mostCommonReason || pattern.primaryReason || pattern.reason || 'Not specified',
          reasonOccurrences: pattern.reasonOccurrences || pattern.reasonCount || 0,
          affectedRestaurantsCount:
            pattern.affectedRestaurantsCount || pattern.restaurantCount || pattern.affectedCount || 0,
          affectedRestaurantIds: pattern.affectedRestaurantIds || pattern.restaurantIds || [],
          affectedRestaurantNames:
            pattern.affectedRestaurantNames ||
            pattern.restaurantNames ||
            pattern.restaurants?.map((r: any) => r.name || r.restaurantName) ||
            [],
          dateRange: pattern.dateRange || pattern.period || 'Recent',
          insight:
            pattern.insight ||
            pattern.analysis ||
            pattern.description ||
            `Recurring ${pattern.itemName || 'item'} issue detected`,
          rootCauseCategory: pattern.rootCauseCategory || pattern.category || pattern.causeType || 'Operational'
        }))
      }
    })

    const transformedSummary: GroupedAbnormalitiesResponse['summary'] = {
      totalRestaurantsAffected: apiSummary.totalAffectedRestaurants || 0,
      totalDistinctItems: undefined, // Not provided by new API
      totalAbnormalities: apiSummary.totalAbnormalities || 0,
      byType: {
        equipment: {
          restaurants: apiSummary.byType?.equipment?.restaurants || 0,
          distinctItems: apiSummary.byType?.equipment?.distinctItems || 0,
          totalItemsDisabled: apiSummary.byType?.equipment?.totalItems || 0
        },
        ingredient: {
          restaurants: apiSummary.byType?.ingredient?.restaurants || 0,
          distinctItems: apiSummary.byType?.ingredient?.distinctItems || 0,
          totalItemsDisabled: apiSummary.byType?.ingredient?.totalItems || 0
        },
        channel: {
          restaurants: apiSummary.byType?.channel?.restaurants || 0,
          distinctItems: apiSummary.byType?.channel?.distinctItems || 0,
          totalItemsDisabled: apiSummary.byType?.channel?.totalItems || 0
        }
      }
    }

    return {
      data: transformedData,
      summary: transformedSummary
    }
  }

  /**
   * Get dashboard summary (if available)
   * Falls back to null if endpoint doesn't exist
   */
  async getDashboardSummary(): Promise<{
    totalRestaurants: number
    activeRestaurants: number
    inactiveRestaurants: number
    equipmentAbnormalitiesCount?: number
    ingredientAbnormalitiesCount?: number
    recentChangesCount?: number
    currentMonthSalesRevenue?: number
    currentMonthTotalOrders?: number
    currentMonthAverageOrderValue?: number
    currentMonthRefundAmount?: number
    currentMonthRefundRate?: number
    activeOffersCount?: number
    scheduledOffersCount?: number
    currentMonthLoyaltyPoints?: number
    currentMonthActiveLoyaltyMembers?: number
    lastUpdated: string
  } | null> {
    try {
      const response = await httpClient.get<{ data: any }>(`${BASE_URL}/dashboard/summary`)

      return response.data.data
    } catch (error) {
      // Endpoint might not exist, return null

      return null
    }
  }

  /**
   * Execute suggestedAction from proactive analysis (separate from AI chat).
   * If your OpenAPI path differs, update this URL to match Swagger.
   */
  async executeProactiveAction(request: ExecuteProactiveActionRequest): Promise<ExecuteProactiveActionResponse> {
    const { restaurantId, actionType, actionPayload } = request
    const response = await httpClient.post<{ data: ExecuteProactiveActionResponse }>(
      `${BASE_URL}/restaurants/${restaurantId}/proactive-analysis/execute-action`,
      { actionType, actionPayload }
    )

    return response.data.data
  }
}

export const aiInsightsApi = new AIInsightsApiService()
